import type { ReactNode } from 'react';
import { Box, Stack, Text, Chip, Skeleton } from '@citibox-panel/ui';
import type { UserSearchHit } from '@citibox-panel/api-client';
import { capitalize } from '../../../lib/capitalize';
import { statusColor } from '../lib/statusColor';
import { formatSignupDate } from '../lib/formatSignupDate';
import { isAnonymized } from '../../../lib/isAnonymized';
import { FieldRow } from './FieldRow';
import { ObservationsBlock } from './ObservationsBlock';

// The three editable fields, as NODES. Same doctrine as `action` below: this component reserves
// where each one lands and nothing about what it is — which is what keeps the draft, the mode and
// the focus in ONE owner (`EditAccountSection`) instead of threading a dozen props through here.
// Its presence is what puts the card in edit mode; absent, this renders exactly as it always has,
// which is what the anonymized ficha depends on.
export interface EditFields {
  name: ReactNode;
  email: ReactNode;
  address: ReactNode;
}

export interface UserLayoutProps {
  account: UserSearchHit | undefined;
  isPending: boolean;
  // The «Dar de baja» button, injected by the host: this component only reserves where it lands
  // (after every field row) and whether it lands at all — never what it is.
  action?: ReactNode;
  // The quiet action on the STATUS ROW, top-right, opposite the chip — «Editar datos» today. A
  // second slot and not a reuse of `action`: the design (capturas de PM, 2026-09-11) puts the two
  // writes of this card in two different places, and one slot cannot hold both. Ordering still
  // reads by ascending consequence — the quiet action first, the destructive one last — it just
  // spans the card instead of a stack.
  headerAction?: ReactNode;
  // The action that acts on the STATE THE CHIP SHOWS — «Bloquear» today — and so is drawn attached
  // to it rather than at the far end of the row. A third slot and not a reuse of `headerAction`:
  // that one is pinned opposite the chip by `justifyContent`, and «beside» and «opposite» are the
  // two ends of the same row. The placement criterion the slice now uses is «the action lives
  // where its result is read», and what a block turns red is the chip an inch to its left.
  statusAction?: ReactNode;
  edit?: EditFields;
}

// A field row whose value is a NODE rather than text. It cannot go through `FieldRow`: that one
// wraps its value in a <p>, and a control (or any <div>) inside a <p> is invalid nesting React
// flags in dev. The label keeps the column's own treatment — bold overline, collapsed leading —
// so an edit row measures and reads exactly like the read row it replaced, and the control is
// named by `aria-label` instead of growing a second visible caption.
// `gap` is for a value drawn in a FRAME: flush against it, the caption reads as part of the box.
function SlotRow({ label, gap, children }: { label: string; gap?: number; children: ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        py: 0.5,
        ...(gap !== undefined ? { gap } : {}),
        '& .MuiTypography-overline': { lineHeight: 1.2, fontWeight: 'bold' },
      }}
    >
      <Text variant="overline" color="secondary">
        {label}
      </Text>
      {children}
    </Box>
  );
}

function AccountSkeleton() {
  return (
    <Stack spacing={1}>
      <Skeleton width={72} height={24} />
      <Skeleton width={220} height={28} />
      {Array.from({ length: 7 }).map((_, i) => (
        <Skeleton key={i} width="80%" />
      ))}
    </Stack>
  );
}

// The account card has no error or empty state of its own: the ficha is always
// reached from a resolved «Usuario», and a fetch failure is routed to the shell
// boundary by useAccount. Render nothing on the unreachable no-account case rather
// than crash on undefined fields.
export function UserLayout({
  account,
  isPending,
  action,
  headerAction,
  statusAction,
  edit,
}: UserLayoutProps) {
  if (isPending) return <AccountSkeleton />;
  if (!account) return null;

  const anonymized = isAnonymized(account.status);

  return (
    <Stack spacing={0.5}>
      {/* A flex row and not a bare box: the chip stays left and the slot sits hard right, on the
          chip's own baseline. `justifyContent` rather than a spacer, so the row collapses back to
          exactly what it was when no slot is passed — which the anonymized ficha depends on. */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        {/* The chip and whatever acts on the state it shows travel as ONE item of the row —
            otherwise `space-between` would fling them to opposite ends, which is the placement
            this slot exists to avoid. Half a step of the grid between them: attached, not merged. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Chip label={capitalize(account.status)} color={statusColor(account.status)} />
          {statusAction}
        </Box>
        {headerAction}
      </Box>
      {/* Identity block: the name with the USER ID as a tight subtitle beneath it
          (label · value inline, not a stacked field row). Name and id share one box
          — so the Stack's field-row spacing does not push them apart — and both
          line-heights are collapsed so the id hugs the name. The id line is bold to
          match the field labels below.
          TODO: i18n — copy en ES hasta Fase 1. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.8,
          '& .MuiTypography-h6': { lineHeight: 1.2, fontWeight: 'bold' },
          '& .MuiTypography-overline': { lineHeight: 1.2, fontWeight: 'bold' },
        }}
      >
        {/* Muted, not the usual verbatim tone, when anonymized: the chip already states the
            status, so this softens the identity itself — a kept trail, not a live person to
            address. The text stays verbatim either way (backend writes «Usuario anonimizado»). */}
        {edit === undefined ? (
          <Text variant="h6" component="h2" {...(anonymized ? { color: 'muted' } : {})}>
            {/* Nameless account (carrier pre-registration) → dash, like empty FieldRows. */}
            {account.name.trim() === '' ? '—' : account.name}
          </Text>
        ) : (
          // The heading is REPLACED, never kept beside the control: two names on screen is two
          // values for the agent to reconcile. It stays above the USER ID line, which is where
          // the heading sat — the identity block is the same block with its first line swapped.
          // UNcaptioned, unlike its two neighbours, and that asymmetry is the design's (captures
          // 2026-09-11): read mode never labelled the name either — it was the heading — so the
          // control takes the heading's place bare, with USER ID still tucked under it. Its
          // accessible name comes from the caller's `aria-label`, so nothing is lost to assistive
          // tech; what is dropped is a caption the read view never had.
          edit.name
        )}
        <Text variant="overline" color="secondary">
          USER ID · {account.id}
        </Text>
      </Box>
      <Box>
        {/* TODO: i18n — copy en ES hasta Fase 1. */}
        <FieldRow label="TELÉFONO" value={account.phone} />
        {edit === undefined ? (
          <FieldRow label="EMAIL" value={account.email} />
        ) : (
          <SlotRow label="EMAIL">{edit.email}</SlotRow>
        )}
        <FieldRow label="COMUNICACIONES" value={account.comms} />
        {/* Read-only in BOTH modes, and the only row of the three that looks editable and is not:
            backend reassigns the matrícula when the location changes ([AS-023]), so the panel
            neither sends it nor lets it be typed — it refetches and paints what came back. */}
        <FieldRow label="MATRÍCULA UBICACIÓN" value={account.locationCode} />
        {edit === undefined ? (
          <FieldRow label="DIRECCIÓN" value={account.address} />
        ) : (
          <SlotRow label="DIRECCIÓN">{edit.address}</SlotRow>
        )}
        <FieldRow label="FECHA DE ALTA" value={formatSignupDate(account.signupDate)} />
        {account.deletionDate !== '' && (
          <FieldRow label="FECHA DE BAJA" value={formatSignupDate(account.deletionDate)} />
        )}
        {account.observations !== '' && (
          // Through SlotRow for the same reason the edit rows are: ObservationsBlock's root is a
          // <div>, and FieldRow would nest it in a <p>.
          <SlotRow label="OBSERVACIONES" gap={1}>
            <ObservationsBlock observations={account.observations} />
          </SlotRow>
        )}
      </Box>
      {/* `pt` and not `mt`, so the air is this slot's own and not an argument with however the
          root `Stack` chooses to distribute its `spacing` — measured as `gap` in this MUI, which
          would make a margin here work today and break on the version that goes back to margins.
          The slot only ever holds «Dar de baja», in both modes, so this is that button's breathing
          room from the last field row. */}
      {action !== undefined && <Box sx={{ pt: 1 }}>{action}</Box>}
    </Stack>
  );
}
